import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BaseService } from '../../models/base/base-service';

/**
 * This samples the refresh rate over time because the BuzzTV app is unreliable during startup, and spits out incorrect
 * refresh rates. Therefore, sample the refresh rate over a couple of seconds, and then take the maximum of those
 * samples to get a more accurate refresh rate.
 */
@Injectable({
  providedIn: 'root'
})
export class RefreshRateService extends BaseService {
  constructor() {
    super();
    this.sampleRefreshRate();
    this.measureOnWindowResize();
  }

  private readonly SAMPLE_N_TIMES = 4;
  private readonly RESIZE_DEBOUNCE_TIME = (this.SAMPLE_N_TIMES - 1) * 1000 + 100; // milliseconds
  private readonly BASE_REFRESH_RATE = 60;

  private refreshRatesOverTime: number[] = [];
  private _refreshRate = new BehaviorSubject<number | null>(this.BASE_REFRESH_RATE);
  public readonly refreshRate$ = this._refreshRate.pipe(distinctUntilChanged());

  sampleRefreshRate(): void {
    Array.from({ length: this.SAMPLE_N_TIMES }, (_, index) => index).forEach((_, i) => {
      setTimeout(() => {
        if (i === this.SAMPLE_N_TIMES - 1) {
          this.calculateRefreshRate();
        } else {
          this.measureDisplayRefreshRate();
        }
      }, i * 1000);
    });
  }

  private measureDisplayRefreshRate(): void {
    let frameCount = 0;
    let startTime: number | null = null;
    const measure = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      frameCount++;
      const elapsed = timestamp - startTime;
      if (elapsed < 1000) {
        requestAnimationFrame(measure);
      } else {
        this.refreshRatesOverTime.push(frameCount);
        console.info(`Display refresh rate measured: ${frameCount} Hz`);
      }
    };
    requestAnimationFrame(measure);
  }

  private calculateRefreshRate(): void {
    const maxRefreshRate =
      this.refreshRatesOverTime.length > 0 ? Math.max(...this.refreshRatesOverTime) : this.BASE_REFRESH_RATE;
    this._refreshRate.next(Math.max(maxRefreshRate));
    this.refreshRatesOverTime = [];
  }

  private measureOnWindowResize(): void {
    fromEvent(window, 'resize')
      .pipe(debounceTime(this.RESIZE_DEBOUNCE_TIME))
      .subscribeWhileAlive({
        owner: this,
        next: () => this.sampleRefreshRate()
      });
  }
}
